import { EntityType } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { ICONS, Package } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { LuCheck, LuPrinter, LuX } from 'react-icons/lu';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useConfirmTransactionPrintLabels from './confirm-transaction-print-labels.controller';

const ID = 'confirm-transaction-print-labels';

const ConfirmTransactionPrintLabels = (props: { wizard_id: string }) => {
    const viewProps = useConfirmTransactionPrintLabels(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled,
        continueLabel: (
            <>
                {CommonMessage.BUTTONS.CONFIRM} {CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
            </>
        )
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.PRINTER.PRINT_LABELS}</div>
            <div className='flex w-full flex-col items-start justify-start gap-3'>
                <div className='text-foreground-default justify-start text-base font-medium'>
                    {CommonMessage.DETAIL.DETAILS_OF(CommonMessage.OBJECTS.TRANSACTION.DEFAULT)}
                </div>
                <div className='flex w-full flex-col items-start justify-start gap-2'>
                    {viewProps.transaction && (
                        <div className='outline-border-default inline-flex w-full flex-col items-start justify-start gap-2 self-stretch rounded-xl p-4 outline-1'>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}</div>
                                <PlatformObjectBadge
                                    className='text-foreground-default flex items-center justify-start gap-1 text-base font-medium'
                                    entityType={EntityType.SPOT}
                                    id={viewProps.transaction.spot_id || undefined}
                                    disableClick
                                    key={viewProps.transaction.spot_id || undefined}
                                />
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.tracking_number}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.purchase_order}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.SUPPLIER.DEFAULT}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.supplier ?? '/'}</div>
                            </div>
                        </div>
                    )}

                    <div className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div className='flex items-center justify-start gap-4'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    {viewProps.collis && viewProps.collis.length > 0 ? (
                                        <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    ) : (
                                        <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    )}
                                    <Package className='size-1/2' />
                                </div>

                                <div className='inline-flex flex-col items-start justify-center gap-1'>
                                    <div className='text-foreground-default justify-start text-base font-semibold'>
                                        {CommonMessage.OBJECTS.PARCEL.PLURAL}{' '}
                                        {viewProps.collis && viewProps.collis?.length > 0 ? `(${viewProps.collis[0].type})` : ''}
                                    </div>
                                    <div className='text-brand-muted justify-start text-base font-medium'>
                                        {viewProps.collis?.length ?? 0} {CommonMessage.OBJECTS.TRANSACTION.ITEMS}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div className='flex items-center justify-start gap-4'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    {viewProps.receivers ? (
                                        <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    ) : (
                                        <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    )}
                                    <ICONS.ENTITY.USER className='size-1/2' />
                                </div>

                                <div className='inline-flex flex-col items-start justify-center gap-1'>
                                    <div className='text-foreground-default justify-start text-base font-semibold'>
                                        {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}
                                    </div>
                                    {viewProps.receivers?.map((receiver) => (
                                        <PlatformObjectBadge
                                            key={receiver.user_id}
                                            id={receiver.user_id}
                                            entityType={EntityType.USER}
                                            className='text-brand-muted flex items-center justify-start gap-1 text-base font-medium'
                                            disableClick
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {viewProps.printer && (
                    <>
                        <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRINTER.DEFAULT}</div>
                        <div className='outline-border-default flex flex-col items-start justify-start gap-4 self-stretch rounded-xl p-4 outline-1'>
                            <div className='inline-flex items-center justify-between self-stretch'>
                                <div className='text-foreground-default justify-start text-base font-semibold'>{viewProps.printer.name}</div>
                            </div>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRINTER.PRINT_TO_CONTINUE}</div>
                            {viewProps.canPrint && (
                                <button
                                    onClick={viewProps.onPrint}
                                    className='outline-brand-primary inline-flex items-center justify-start gap-2 rounded-md px-4 py-3 outline-2 outline-offset-[-2px]'>
                                    {viewProps.printing ? <Spinner /> : <LuPrinter className='text-brand-primary size-5' />}

                                    <div className='text-brand-primary justify-start text-base font-medium'>{CommonMessage.OBJECTS.PRINTER.PRINT_LABELS}</div>
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ConfirmTransactionPrintLabels;
